import * as React from 'react';
import MAX_CARDS from './shared/Constants';
import OrderSummary from './shared/OrderSummaryBox';
import DvlaErrors from '../validation/DvlaErrors';

interface Props {
  presenter: {
    errors: Record<string, string[]>;
    callCharges: string;
    phoneNumber: string;
    openingTimes: string;
    numCards: number;
    transactionCost: number;
    question: string | null;
    questionHint: {
      __html: string;
    };
    action: string;
    paramName: string;
    csrfParam: string;
    csrfToken: string;
  };
}

interface State {
  calculating: boolean;
  errors: DvlaErrors;
  numCards: number;
  runningTotal: string;
  validatedTachoDisplayNum: number;
}

function isInteger(n: number): boolean {
  return n % 1 === 0;
}

function convertPennysToPounds(penny: number): string {
  return (penny / 100).toFixed(2).toLocaleString();
}

function isValidNumCards(value: number): boolean {
  return value > 0 && value <= MAX_CARDS && isInteger(value);
}

export default class CardsPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      calculating: false,
      errors: new DvlaErrors(this.props.presenter.errors),
      numCards: this.props.presenter.numCards,
      ...this.calculateOrderSummary(this.props.presenter.numCards),
    };

    this.handleNewTotal = this.handleNewTotal.bind(this);
  }

  calculateOrderSummary(value: number) {
    return isValidNumCards(value)
      ? {
          runningTotal: convertPennysToPounds(
            value * this.props.presenter.transactionCost,
          ),
          validatedTachoDisplayNum: value,
        }
      : { runningTotal: '0', validatedTachoDisplayNum: 0 };
  }

  handleNewTotal(event) {
    const value = event.target.value;

    this.setState({
      numCards: value,
      ...this.calculateOrderSummary(value),
    });
  }

  render() {
    return (
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          <form action={this.props.presenter.action} method="post">
            <div
              className={
                this.state.errors.includes('num_cards')
                  ? 'govuk-form-group govuk-form-group--error'
                  : 'govuk-form-group'
              }
            >
              <h1 className="govuk-label-wrapper">
                <label
                  className="govuk-label govuk-label--l"
                  htmlFor={`${this.props.presenter.paramName}_num_cards`}
                >
                  {this.props.presenter.question ??
                    'How many company tachograph cards do you require?'}
                </label>
                <div
                  className="govuk-hint"
                  dangerouslySetInnerHTML={this.props.presenter.questionHint}
                />
              </h1>
              {this.state.errors
                .messagesFor('num_cards')
                .slice(0, 1)
                .map((error, i) => (
                  <span
                    id={`num_cards_${i}_error`}
                    key={i}
                    className="govuk-error-message"
                  >
                    {error}
                  </span>
                ))}
              <input
                className="govuk-input govuk-input--width-4"
                id={`${this.props.presenter.paramName}_num_cards`}
                name={`${this.props.presenter.paramName}[num_cards]`}
                value={this.state.numCards}
                type="text"
                onChange={this.handleNewTotal}
              />
            </div>
            <div className="govuk-form-group">
              <input
                type="hidden"
                name={this.props.presenter.csrfParam}
                value={this.props.presenter.csrfToken}
                readOnly
              />
            </div>
            <button id="continue" type="submit" className="govuk-button">
              Continue
            </button>
          </form>
        </div>
        <div className="govuk-grid-column-one-third">
          <details className="govuk-details">
            <summary className="govuk-details__summary">
              <span className="govuk-details__summary-text">Need help</span>
            </summary>
            <div className="govuk-details__text">
              <h2 className="heading-medium" style={{ marginTop: 0 }}>
                Call DVLA
              </h2>
              <p>
                {this.props.presenter.phoneNumber}
                <br />
                {this.props.presenter.openingTimes}
              </p>
              <a
                href={this.props.presenter.callCharges}
                target="_blank"
                rel="noopener noreferrer"
              >
                Find out about call charges
              </a>
            </div>
          </details>
          <OrderSummary
            runningTotal={this.state.runningTotal}
            validatedPurchaseVolume={this.state.validatedTachoDisplayNum}
          />
        </div>
      </div>
    );
  }
}
